import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Download, Eye, Heart } from "lucide-react";
import { isStaff, type ResultEnvelope } from "@cd/core";
import { capabilities } from "../../lib/access";
import { api, type MyListing } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge, Card, Skeleton } from "../../components/ui/primitives";
import { Chart } from "../../components/viz/Chart";
import { KindBadge, plural, VerifiedBadge } from "./ListingCard";

const TREND_DAYS = 30;
const TREND_VIZ = { type: "line" as const, config: {} };

/** What you've published, or with `all` (super users) everyone's listings most popular first, with installs per day over the last 30 days. */
export function MyListings({ all = false }: { all?: boolean }) {
  const mine = useQuery({ queryKey: all ? ["marketplace-mine", "all"] : ["marketplace-mine"], queryFn: all ? api.allListingStats : api.myListings });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  if (mine.isLoading) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-40" />)}</div>;
  if (mine.error) return <p className="text-sm text-bad">{mine.error.message}</p>;
  if (!mine.data?.length) {
    if (all) return <p className="mt-16 text-center text-sm text-muted">Nothing has been published yet.</p>;
    return (
      <p className="mt-16 text-center text-sm text-muted">
        {me.data && !isStaff(me.data)
          ? "Publishing to the marketplace is limited to GoTab staff for now."
          : "You haven't published anything yet. Use “Publish” on a dashboard, or “Publish widget” in a widget's menu."}
      </p>
    );
  }
  // Relisting is staff moderation; not while viewing as someone.
  const staff = capabilities(me.data).own && (me.data ? isStaff(me.data) : false);
  return <div className="space-y-3">{mine.data.map((l) => <MyListingRow key={l.id} listing={l} staff={staff} showPublisher={all} />)}</div>;
}

function MyListingRow({ listing: l, staff, showPublisher }: { listing: MyListing; staff: boolean; showPublisher: boolean }) {
  const qc = useQueryClient();
  const relist = useMutation({
    mutationFn: () => api.moderate(l.id, { status: "listed" }),
    onSuccess: (d) => {
      qc.setQueryData(["listing", l.id], d);
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      qc.invalidateQueries({ queryKey: ["marketplace-mine"] });
      qc.invalidateQueries({ queryKey: ["marketplace-facets"] });
    },
  });
  const recent = l.trend.reduce((a, d) => a + d.installs, 0);
  return (
    <Card className="flex flex-col gap-4 p-4 md:flex-row">
      <div className="min-w-0 md:w-72">
        <div className="flex flex-wrap items-center gap-1.5">
          <KindBadge kind={l.kind} />
          {l.verified && <VerifiedBadge />}
          {l.featured && <Badge tone="accent">Featured</Badge>}
          {l.status === "unlisted" && <Badge tone="warn">{showPublisher ? "Unlisted" : "Unlisted by GoTab"}</Badge>}
          {l.status === "unlisted" && staff && (
            <Button size="sm" disabled={relist.isPending} onClick={() => relist.mutate()}><Eye className="size-3" /> Relist</Button>
          )}
        </div>
        {relist.error && <p className="mt-1 text-xs text-bad">{relist.error.message}</p>}
        <Link to="/marketplace/$id" params={{ id: l.id }} className="mt-2 block truncate text-sm font-semibold hover:underline">{l.title}</Link>
        <p className="text-xs text-subtle">{showPublisher && `${l.publisher} · `}v{l.version} · updated {new Date(l.updatedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>
        <div className="mt-3 flex gap-4 text-xs text-muted">
          <span className="inline-flex items-center gap-1"><Download className="size-3" /> {plural(l.installers, "installer")}</span>
          <span className="inline-flex items-center gap-1"><Heart className="size-3" /> {plural(l.likes, "like")}</span>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-subtle">{plural(recent, "install")} in the last {TREND_DAYS} days</p>
        <div className="h-32"><InstallTrend trend={l.trend} /></div>
      </div>
    </Card>
  );
}

/** Installs per day as a single-series line, with days that had none filled in as zero. */
function InstallTrend({ trend }: { trend: MyListing["trend"] }) {
  const result = useMemo<ResultEnvelope>(() => {
    const byDay = new Map(trend.map((d) => [d.day, d.installs]));
    const today = new Date();
    const rows = Array.from({ length: TREND_DAYS }, (_, i) => {
      const day = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (TREND_DAYS - 1 - i))).toISOString().slice(0, 10);
      return { day, installs: byDay.get(day) ?? 0 };
    });
    return {
      columns: [
        { key: "day", label: "Day", kind: "dimension", type: "time", grain: "day" },
        { key: "installs", label: "Installs", kind: "metric", type: "number", format: "integer" },
      ],
      rows,
      warnings: [],
      provenance: { metrics: [], dimensions: [], joinPaths: [], filters: [], scope: { locationCount: 0, description: "" }, registryVersion: "" },
      certified: false,
      asOf: today.toISOString(),
      cached: false,
      durationMs: 0,
    };
  }, [trend]);
  return <Chart viz={TREND_VIZ} result={result} />;
}
